/**
 * Sign-in — one calm page, three ways in (PRD 04 §3).
 *
 * The page itself is the design system's `SignInPanel`; what lives here is the Studio's
 * side of it — the copy in the parent's voice (EN + TR), the provider's five actions, and
 * the error matrix (`errors.ts`) turned into lines. On success the page yields to the
 * route the person was heading for, or the library.
 *
 * Under the card, language and light/dark (`i-0003`). Both live in the account menu, which
 * is behind the shell — so until this page they could not be reached at all, and a parent
 * who reads Turkish met an English page with no way out of it. They sit **under** the card
 * rather than in it because they are the page's chrome, not a fourth way in; and they are
 * the Studio's rather than the shared panel's, because the admin app wears that panel and
 * is English-only with no theme of its own.
 */

import { Card, Segmented, SignInPanel, Wordmark } from '@atlas-nook/design-system';
import type { SignInActions, SignInProblem, SignInStrings } from '@atlas-nook/design-system';
import { useMemo } from 'react';
import type { JSX } from 'react';
import { Navigate, useLocation } from 'react-router';

import { EMULATOR, studioEnv } from '../env.js';
import { useI18n, useT } from '../i18n/I18nProvider.js';
import type { Locale, MessageKey, Translate } from '../i18n/I18nProvider.js';
import { useDocumentTitle } from '../shell/useDocumentTitle.js';
import { useTheme } from '../shell/ThemeProvider.js';
import type { ThemeChoice } from '../shell/ThemeProvider.js';
import { useAuth } from './AuthProvider.js';
import { classifyAuthError } from './errors.js';
import type { AuthProblemKind } from './errors.js';

/** The matrix, as copy: code → line. `accountExists` picks its line by the method offered. */
const PROBLEM_KEY: Record<Exclude<AuthProblemKind, 'accountExists'>, MessageKey> = {
  badCredentials: 'signin.errBadCredentials',
  emailInUse: 'signin.errEmailInUse',
  weakPassword: 'signin.errWeakPassword',
  tooMany: 'signin.errTooMany',
  network: 'signin.errNetwork',
  invalidEmail: 'signin.errInvalidEmail',
  popup: 'signin.errPopup',
  methodOff: 'signin.errMethodOff',
  recentLogin: 'signin.errRecentLogin',
  failed: 'signin.failed',
};

export function describeAuthError(error: unknown, t: Translate): SignInProblem {
  const problem = classifyAuthError(error);
  if (problem.kind === 'accountExists') {
    const method = problem.method ?? 'password';
    return {
      message: t(
        method === 'link' ? 'signin.errAccountExistsLink' : 'signin.errAccountExistsPassword',
      ),
      offer: method,
      ...(problem.email !== undefined && problem.email !== '' ? { email: problem.email } : {}),
    };
  }
  return { message: t(PROBLEM_KEY[problem.kind]) };
}

/**
 * The two chrome choices, in the account menu's own words — the strings are already there in
 * both catalogs, so this page adds no copy and the i18n parity test has nothing new to hold.
 */
const LANGUAGES: { value: Locale; key: MessageKey }[] = [
  { value: 'en', key: 'account.languageEn' },
  { value: 'tr', key: 'account.languageTr' },
];

const THEMES: { value: ThemeChoice; key: MessageKey }[] = [
  { value: 'system', key: 'account.themeSystem' },
  { value: 'light', key: 'account.themeLight' },
  { value: 'dark', key: 'account.themeDark' },
];

/** Where to land after signing in: where the person was going, else the library. */
function useDestination(): string {
  const location = useLocation();
  const from = (location.state as { from?: unknown } | null)?.from;
  return typeof from === 'string' && from.startsWith('/') && from !== '/signin' ? from : '/library';
}

export function SignIn(): JSX.Element {
  const t = useT();
  const { locale, setLocale } = useI18n();
  const { theme, setTheme } = useTheme();
  useDocumentTitle([t('signin.title')]);
  const auth = useAuth();
  const destination = useDestination();
  const useEmulator = studioEnv().useEmulator;

  const strings = useMemo<SignInStrings>(
    () => ({
      title: t('signin.title'),
      lead: t('signin.lead'),
      email: t('signin.email'),
      emailPlaceholder: t('signin.emailPlaceholder'),
      emailRequired: t('signin.emailRequired'),
      sendLink: t('signin.send'),
      sending: t('signin.sending'),
      usePassword: t('signin.usePassword'),
      password: t('signin.password'),
      passwordRequired: t('signin.passwordRequired'),
      passwordTooShort: t('signin.passwordTooShort'),
      signIn: t('signin.signIn'),
      create: t('signin.create'),
      forgot: t('signin.forgot'),
      working: t('signin.working'),
      or: t('signin.or'),
      google: t('signin.google'),
      sentTitle: t('signin.sentTitle'),
      sentBody: (email) => t('signin.sentBody', { email }),
      sentAgain: t('signin.sentAgain'),
      resetTitle: t('signin.resetTitle'),
      resetBody: (email) => t('signin.resetBody', { email }),
      back: t('signin.back'),
      confirmTitle: t('signin.confirmEmailTitle'),
      confirmBody: t('signin.confirmEmailBody'),
      confirmAction: t('signin.confirmEmailAction'),
      finishing: t('signin.finishing'),
      offerPassword: t('signin.offerPassword'),
      offerLink: t('signin.offerLink'),
      ...(useEmulator ? { emulator: t('signin.emulator', { url: EMULATOR.authUrl }) } : {}),
    }),
    [t, useEmulator],
  );

  const actions = useMemo<SignInActions>(
    () => ({
      sendLink: auth.sendLink,
      completeLink: auth.completeLink,
      signInWithPassword: auth.signInWithPassword,
      createAccount: auth.createAccount,
      resetPassword: auth.resetPassword,
      signInWithGoogle: auth.signInWithGoogle,
      describe: async (error) => describeAuthError(error, t),
    }),
    [auth, t],
  );

  if (auth.session !== undefined && auth.session !== null) {
    return <Navigate to={destination} replace />;
  }

  return (
    <main
      className="surface surface-center"
      id="main"
      style={{ minHeight: '100vh', overflow: 'auto' }}
    >
      <Card raised>
        <SignInPanel
          strings={strings}
          actions={actions}
          isLinkArrival={auth.isLinkArrival}
          pendingEmail={auth.pendingEmail}
        >
          <Wordmark />
        </SignInPanel>
      </Card>

      {/* A sibling of the card, not a child: `.surface` is already a centred column, so the
          row lands under it without a wrapper and without disturbing the centring. */}
      <div className="signin-chrome">
        <Segmented
          label={t('account.language')}
          value={locale}
          options={LANGUAGES.map(({ value, key }) => ({ value, label: t(key) }))}
          onChange={setLocale}
        />
        <Segmented
          label={t('account.theme')}
          value={theme}
          options={THEMES.map(({ value, key }) => ({ value, label: t(key) }))}
          onChange={setTheme}
        />
      </div>
    </main>
  );
}
